import { useEffect, useMemo, useState } from "react";
import api, { apiError, brl, maskDoc, printAgent, rows } from "@/lib/api";
import { toast } from "sonner";
import { Modal } from "@/components/panel/ui";
import { Button, Checkbox, Field, Input, Textarea } from "@/components/ui-kit";

/**
 * Emissão manual de NF-e ou NFC-e, direto da tela Fiscal.
 *
 * Dois modos, seguindo o desenho pedido:
 * - "Por pedido(s)": NFC-e vincula UMA venda/pedido; NF-e permite vários pedidos
 *   do mesmo cliente, somando o total (com valor manual opcional que consolida
 *   tudo num item só).
 * - "Avulsa": descrição + valor, sem vínculo com pedido.
 */
// Status que significam falha — a nota foi criada, mas não autorizada.
const ERROR_STATUS = ["erro_autorizacao", "rejeitada", "denegada", "erro"];

export default function EmitInvoiceModal({ kind, open, onClose, onEmitted }) {
  const isNfe = kind === "nfe";
  const label = isNfe ? "Nota Fiscal NF-e" : "Cupom Fiscal NFC-e";

  const [mode, setMode] = useState("orders");
  const [busy, setBusy] = useState(false);
  const [print, setPrint] = useState(false);

  // avulsa
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [avName, setAvName] = useState("");
  const [avDoc, setAvDoc] = useState("");
  /* Endereço do destinatário — só a NF-e precisa.
     O schema do modelo 55 exige o grupo `enderDest` dentro de `dest`; faltando,
     a SEFAZ recusa com "Erro na validação do Schema XML", que não diz que o que
     falta é endereço. A NFC-e (modelo 65) sai só com CPF, e é por isso que o
     balcão nunca esbarrou nisso. */
  const [avEnd, setAvEnd] = useState({
    logradouro: "", numero: "", complemento: "", bairro: "", municipio: "", uf: "", cep: "",
  });
  const setEnd = (campo) => (e) => setAvEnd((p) => ({ ...p, [campo]: e.target.value }));

  // Quadro "Dados adicionais" da DANFE (infCpl) — vale para os dois modos.
  const [obs, setObs] = useState("");

  // por pedido(s)
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [customer, setCustomer] = useState(null); // { id, name, document }
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [manualAmount, setManualAmount] = useState("");
  const [manualDesc, setManualDesc] = useState("");

  const reset = () => {
    setMode("orders"); setPrint(false);
    setDescription(""); setAmount(""); setAvName(""); setAvDoc(""); setObs("");
    setCustQuery(""); setCustResults([]); setCustomer(null); setOrders([]);
    setSelected(new Set()); setManualAmount(""); setManualDesc("");
  };
  useEffect(() => { if (open) reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Busca de cliente (debounce) — clientes com pedidos vêm do CRM.
  useEffect(() => {
    if (customer || custQuery.trim().length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => {
      api.get("/admin/customers", { params: { search: custQuery, per_page: 8 } })
        .then((r) => setCustResults(rows(r))).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [custQuery, customer]);

  // Ao escolher o cliente, carrega os pedidos faturáveis dele.
  const pickCustomer = async (c) => {
    setCustomer(c); setCustResults([]); setCustQuery("");
    try {
      const r = await api.get("/admin/orders", {
        params: { user_id: c.id, pending_invoice: 1, per_page: 50 },
      });
      setOrders(rows(r));
      // NFC-e é de uma venda só: não pré-seleciona nada; NF-e idem, o operador marca.
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      // NFC-e vincula um pedido só: trocar a seleção substitui.
      if (!isNfe) { next.clear(); if (!prev.has(id)) next.add(id); return next; }
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedOrders = orders.filter((o) => selected.has(o.id));
  const ordersSum = useMemo(() => selectedOrders.reduce((s, o) => s + Number(o.total || 0), 0), [selectedOrders]);
  const manualNum = parseFloat(String(manualAmount).replace(",", "."));
  const previewTotal = mode === "orders"
    ? (isNfe && manualAmount ? manualNum : ordersSum)
    : parseFloat(String(amount).replace(",", "."));

  // A NF-e avulsa precisa do destinatário INTEIRO. Deixar emitir sem ele só
  // gastaria uma tentativa recusada pela SEFAZ, que é o que vinha acontecendo.
  const destinatarioNfeCompleto = !isNfe || (
    avDoc.replace(/\D/g, "").length >= 11
    && ["logradouro", "numero", "bairro", "municipio", "uf", "cep"].every((c) => avEnd[c].trim())
  );

  const canEmit = mode === "avulsa"
    ? !!(description.trim() && parseFloat(String(amount).replace(",", ".")) > 0 && destinatarioNfeCompleto)
    : selected.size > 0;

  const emit = async () => {
    if (busy || !canEmit) return;
    setBusy(true);
    try {
      const body = mode === "avulsa"
        ? { type: kind, mode: "avulsa", description: description.trim(),
            amount: parseFloat(String(amount).replace(",", ".")),
            customer_name: avName || undefined, customer_document: avDoc.replace(/\D/g, "") || undefined,
            // Só na NF-e: mandar endereço na NFC-e não faz mal, mas o campo nem
            // é coletado lá, e enviar vazio suja o payload.
            ...(isNfe ? {
              customer_logradouro: avEnd.logradouro.trim() || undefined,
              customer_numero: avEnd.numero.trim() || undefined,
              customer_complemento: avEnd.complemento.trim() || undefined,
              customer_bairro: avEnd.bairro.trim() || undefined,
              customer_municipio: avEnd.municipio.trim() || undefined,
              customer_uf: avEnd.uf.trim().toUpperCase() || undefined,
              customer_cep: avEnd.cep.replace(/\D/g, "") || undefined,
            } : {}) }
        : { type: kind, mode: "orders", order_ids: [...selected],
            customer_name: customer?.name || undefined,
            customer_document: (customer?.document || "").replace(/\D/g, "") || undefined,
            ...(isNfe && manualAmount ? { amount: manualNum, description: manualDesc || undefined } : {}) };

      if (obs.trim()) body.informacoes_complementares = obs.trim();

      const { data } = await api.post("/admin/invoices/emit-manual", body);

      // A emissão é assíncrona: a SEFAZ pode recusar já na resposta. Sem checar o
      // status, a tela diria "enviada" para uma nota que na verdade falhou.
      if (ERROR_STATUS.includes(data.status)) {
        toast.error(data.status_message || "A SEFAZ recusou a emissão.", { duration: 8000 });
        onEmitted?.(); // a nota recusada aparece na lista com o motivo
        return;        // mantém o modal aberto para reemitir depois de corrigir
      }

      toast.success(`${isNfe ? "NF-e" : "NFC-e"} nº ${data.number} enviada para autorização`);
      if (print) {
        try { await printAgent.printApiDoc(`/invoices/${data.id}/pdf`); }
        catch (e) { toast.error(`Emitida, mas falhou ao imprimir: ${e.message}`); }
      }
      onEmitted?.();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const tabCls = (m) => `flex-1 px-4 h-10 rounded-lg text-sm font-semibold transition-colors ${
    mode === m ? "bg-brand-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-brand-400"}`;

  return (
    <Modal open={open} onClose={onClose} title={`Emitir ${label}`} maxWidth="max-w-lg" testId={`emit-modal-${kind}`}>
      <p className="text-sm text-slate-500 -mt-2 mb-4">
        {isNfe ? "Vincule um ou mais pedidos, ou emita avulsa (descrição + valor)."
               : "Vincule uma venda/pedido, ou emita avulsa (descrição + valor)."}
      </p>

      <div className="flex gap-2 mb-5" role="tablist" aria-label="Modo de emissão">
        <button type="button" role="tab" aria-selected={mode === "orders"} onClick={() => setMode("orders")} className={tabCls("orders")} data-testid="emit-mode-orders">
          {isNfe ? "Por pedidos" : "Por pedido"}
        </button>
        <button type="button" role="tab" aria-selected={mode === "avulsa"} onClick={() => setMode("avulsa")} className={tabCls("avulsa")} data-testid="emit-mode-avulsa">
          Avulsa (descrição + valor)
        </button>
      </div>

      {mode === "avulsa" ? (
        <div className="space-y-3" data-testid="emit-avulsa-form">
          <Field label="Descrição" required>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="emit-avulsa-description"
              placeholder="Ex.: Venda de mercadorias / Serviço prestado" />
          </Field>
          <Field label="Valor (R$)" required>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="emit-avulsa-amount" mono />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome do cliente" hint="Opcional">
              <Input value={avName} onChange={(e) => setAvName(e.target.value)} data-testid="emit-avulsa-name" />
            </Field>
            <Field
              label="CPF/CNPJ"
              required={isNfe}
              hint={isNfe ? "Obrigatório na NF-e" : "Em branco: consumidor não identificado"}
            >
              <Input value={avDoc} onChange={(e) => setAvDoc(maskDoc(e.target.value))} data-testid="emit-avulsa-doc" mono />
            </Field>
          </div>

          {/* A NF-e não sai sem endereço do destinatário. Não é regra nossa: é o
              schema do modelo 55, e a recusa da SEFAZ ("Erro na validação do
              Schema XML") não diz qual campo falta. */}
          {isNfe && (
            <div className="space-y-3 border-t border-slate-200 pt-3" data-testid="emit-avulsa-endereco">
              <p className="text-xs text-slate-500">
                <b className="text-slate-700">Endereço do destinatário.</b>{" "}
                A NF-e é recusada sem ele — a NFC-e, não.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Logradouro" required className="col-span-2">
                  <Input value={avEnd.logradouro} onChange={setEnd("logradouro")} data-testid="emit-avulsa-logradouro" />
                </Field>
                <Field label="Número" required>
                  <Input value={avEnd.numero} onChange={setEnd("numero")} data-testid="emit-avulsa-numero" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Complemento" hint="Opcional">
                  <Input value={avEnd.complemento} onChange={setEnd("complemento")} data-testid="emit-avulsa-complemento" />
                </Field>
                <Field label="Bairro" required>
                  <Input value={avEnd.bairro} onChange={setEnd("bairro")} data-testid="emit-avulsa-bairro" />
                </Field>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <Field label="Município" required className="col-span-2">
                  <Input value={avEnd.municipio} onChange={setEnd("municipio")} data-testid="emit-avulsa-municipio" />
                </Field>
                <Field label="UF" required>
                  <Input value={avEnd.uf} maxLength={2} onChange={setEnd("uf")} data-testid="emit-avulsa-uf" mono />
                </Field>
                <Field label="CEP" required>
                  <Input value={avEnd.cep} onChange={setEnd("cep")} data-testid="emit-avulsa-cep" mono />
                </Field>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="emit-orders-form">
          {/* Seleção de cliente */}
          {customer ? (
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
              <span className="text-sm"><b className="text-slate-900">{customer.name}</b>
                {customer.document && <span className="text-slate-400"> · {maskDoc(customer.document)}</span>}</span>
              <button type="button" onClick={() => { setCustomer(null); setOrders([]); setSelected(new Set()); }}
                data-testid="emit-change-customer" className="text-xs font-semibold text-brand-600 hover:underline">Trocar</button>
            </div>
          ) : (
            <Field label="Cliente" required className="relative">
              <Input value={custQuery} onChange={(e) => setCustQuery(e.target.value)} data-testid="emit-customer-search"
                placeholder="Busque pelo nome, e-mail ou CPF do cliente"
                role="combobox" aria-expanded={custResults.length > 0} aria-autocomplete="list" />
              {custResults.length > 0 && (
                <ul className="absolute z-30 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg">
                  {custResults.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => pickCustomer(c)} data-testid="emit-customer-option"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
                        {c.name} {c.document && <span className="text-slate-400">· {maskDoc(c.document)}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Field>
          )}

          {/* Pedidos do cliente — grupo de opções, então fieldset/legend em vez
              de um <label> solto apontando para nada. */}
          {customer && (
            <fieldset>
              <legend className="text-eyebrow uppercase tracking-wider text-slate-500">
                {isNfe ? "Pedidos a vincular" : "Pedido a vincular"}
                <span className="text-red-500 ml-0.5" aria-hidden>*</span>
                <span className="sr-only"> (obrigatório)</span>
              </legend>
              <div className="mt-1.5 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto">
                {orders.map((o) => (
                  <label key={o.id} data-testid="emit-order-option"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-slate-50">
                    <input type={isNfe ? "checkbox" : "radio"} checked={selected.has(o.id)} onChange={() => toggle(o.id)}
                      aria-label={`Pedido ${o.number} · ${brl(o.total)}`}
                      data-testid="emit-order-checkbox" className="accent-brand-600" />
                    <span className="font-mono text-slate-900">{o.number}</span>
                    <span className="text-slate-400 text-xs">{new Date(o.created_at).toLocaleDateString("pt-BR")}</span>
                    <span className="ml-auto font-mono text-slate-700">{brl(o.total)}</span>
                  </label>
                ))}
                {orders.length === 0 && <p className="px-3 py-3 text-sm text-slate-400" data-testid="emit-no-orders">Nenhum pedido faturável para este cliente.</p>}
              </div>
            </fieldset>
          )}

          {/* Valor manual (só NF-e) */}
          {isNfe && customer && selected.size > 0 && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60">
              <p className="text-xs text-slate-500 mb-2">Valor manual <span className="text-slate-400">(opcional)</span>: emite a nota com este valor num item só, mantendo os pedidos como faturados.</p>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" min="0" step="0.01" value={manualAmount} onChange={(e) => setManualAmount(e.target.value)}
                  data-testid="emit-manual-amount" placeholder={`Calculado: ${brl(ordersSum)}`}
                  aria-label="Valor manual da nota" mono />
                <Input value={manualDesc} onChange={(e) => setManualDesc(e.target.value)} data-testid="emit-manual-desc"
                  placeholder="Descrição (opcional)" aria-label="Descrição do valor manual" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quadro "Dados adicionais" da DANFE (infCpl). Vale para avulsa e para
          pedido: é texto livre do contribuinte — número do pedido, condição de
          pagamento, prazo de garantia, observação da entrega. */}
      <Field label="Dados adicionais" className="mt-4"
        hint={`Aparece no quadro "Informações adicionais" da nota. ${500 - obs.length} caracteres restantes.`}>
        <Textarea value={obs} onChange={(e) => setObs(e.target.value.slice(0, 500))} rows={2}
          placeholder="Ex.: Garantia de 12 meses. Pedido 4821." data-testid="emit-obs" />
      </Field>

      <Checkbox
        checked={print}
        onChange={(e) => setPrint(e.target.checked)}
        data-testid="emit-print"
        className="mt-4 text-slate-600"
        label={`Imprimir o ${isNfe ? "DANFE" : "cupom"} ao emitir`}
      />

      <Button onClick={emit} disabled={!canEmit} loading={busy} size="xl" block className="mt-4" data-testid="emit-submit">
        {busy ? "Emitindo..." : `Emitir ${isNfe ? "nota" : "cupom"}${previewTotal > 0 ? ` · ${brl(previewTotal)}` : ""}`}
      </Button>
    </Modal>
  );
}
